import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Shield,
  BarChart3, RefreshCw, Clock, CheckCircle, Bell, BellOff,
  ChevronRight, Zap
} from "lucide-react";

const TrendAnalysisPage = () => {
  const { t } = useTranslation('platform');
  const { user } = useAuth();
  const [trends, setTrends] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [days, setDays] = useState(180);
  const [runningCheck, setRunningCheck] = useState(false);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/trends?days=${days}`, { withCredentials: true });
      setTrends(res.data);
    } catch (err) {
      console.error("Trend fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/analytics/alerts`, { withCredentials: true });
      setAlerts(res.data);
    } catch (err) {
      console.error("Alerts fetch error:", err);
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => { fetchTrends(); fetchAlerts(); }, [days]);

  const runAlertCheck = async () => {
    setRunningCheck(true);
    try {
      await axios.post(`${API}/analytics/alerts/run-check`, {}, { withCredentials: true });
      await fetchAlerts();
    } catch (err) {
      console.error(err);
    } finally {
      setRunningCheck(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/analytics/alerts/read-all`, {}, { withCredentials: true });
      await fetchAlerts();
    } catch (err) {
      console.error(err);
    }
  };

  const trajectoryIcon = (tr) => {
    if (tr === "improving") return <TrendingUp className="text-emerald-400" size={20} />;
    if (tr === "declining") return <TrendingDown className="text-red-400" size={20} />;
    return <Minus className="text-amber-400" size={20} />;
  };

  const severityColor = (s) => ({
    critical: "border-red-500 bg-red-500/10 text-red-400",
    high: "border-amber-500 bg-amber-500/10 text-amber-400",
    medium: "border-blue-500 bg-blue-500/10 text-blue-400",
    low: "border-slate-500 bg-slate-500/10 text-slate-400",
  }[s] || "border-slate-500 bg-slate-500/10 text-slate-400");

  const riskColor = (score) => {
    if (score <= 30) return "#10B981";
    if (score <= 60) return "#C9A84C";
    return "#EF4444";
  };

  if (loading && !trends) {
    return (
      <QMSLayout title={t('trendAnalysis.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="logo-hex w-12 h-12 animate-pulse" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('trendAnalysis.title')}>
      <div className="space-y-6" data-testid="trend-analysis-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-['Syne'] text-2xl font-bold text-white">{t('trendAnalysis.heading')}</h2>
            <p className="text-[#8B95A5] text-sm mt-1">{t('trendAnalysis.headingSubtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
              data-testid="trend-period-select"
            >
              <option value={30}>{t('trendAnalysis.last30Days')}</option>
              <option value={90}>{t('trendAnalysis.last90Days')}</option>
              <option value={180}>{t('trendAnalysis.last6Months')}</option>
              <option value={365}>{t('trendAnalysis.lastYear')}</option>
            </select>
            <button onClick={fetchTrends} className="flex items-center gap-2 px-3 py-2 bg-[#181B23] text-[#C9A84C] border border-[#C9A84C]/30 rounded text-sm hover:bg-[#C9A84C]/10 transition-colors" data-testid="refresh-trends-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t('trendAnalysis.refresh')}
            </button>
          </div>
        </div>

        {/* Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Risk Score */}
          <div className="qms-card" data-testid="risk-score-card">
            <div className="qms-card-body flex items-center gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ border: `3px solid ${trends?.data_maturity === "onboarding" ? "#555" : riskColor(trends?.risk_score || 0)}` }}>
                <span className="text-xl font-bold" style={{ color: trends?.data_maturity === "onboarding" ? "#555" : riskColor(trends?.risk_score || 0) }}>
                  {trends?.data_maturity === "onboarding" ? "—" : (trends?.risk_score || 0)}
                </span>
              </div>
              <div>
                <p className="text-[#8B95A5] text-xs uppercase tracking-wider">{t('trendAnalysis.riskScore')}</p>
                <p className="text-white font-semibold">
                  {trends?.data_maturity === "onboarding"
                    ? "Awaiting Data"
                    : trends?.risk_score <= 30 ? t('trendAnalysis.lowRisk') : trends?.risk_score <= 60 ? t('trendAnalysis.moderate') : t('trendAnalysis.highRisk')}
                </p>
              </div>
            </div>
          </div>

          {/* Trajectory */}
          <div className="qms-card" data-testid="trajectory-card">
            <div className="qms-card-body flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#181B23] flex items-center justify-center">
                {trends?.trajectory === "insufficient_data" || trends?.trajectory === "baseline"
                  ? <BarChart3 className="text-slate-500" size={24} />
                  : trajectoryIcon(trends?.trajectory)}
              </div>
              <div>
                <p className="text-[#8B95A5] text-xs uppercase tracking-wider">{t('trendAnalysis.trajectory')}</p>
                <p className="text-white font-semibold capitalize">
                  {trends?.trajectory === "insufficient_data" ? "Needs More Data"
                    : trends?.trajectory === "baseline" ? "Building Baseline"
                    : trends?.trajectory || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Overdue Tasks */}
          <div className="qms-card" data-testid="overdue-tasks-card">
            <div className="qms-card-body flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#181B23] flex items-center justify-center">
                <Clock className="text-amber-400" size={24} />
              </div>
              <div>
                <p className="text-[#8B95A5] text-xs uppercase tracking-wider">{t('trendAnalysis.overdueTasks')}</p>
                <p className="text-white text-xl font-bold">{trends?.overdue_tasks || 0}</p>
              </div>
            </div>
          </div>

          {/* Unread Alerts */}
          <div className="qms-card" data-testid="unread-alerts-card">
            <div className="qms-card-body flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#181B23] flex items-center justify-center">
                <Bell className={alerts.unread_count > 0 ? "text-red-400" : "text-slate-500"} size={24} />
              </div>
              <div>
                <p className="text-[#8B95A5] text-xs uppercase tracking-wider">{t('trendAnalysis.unreadAlerts')}</p>
                <p className="text-white text-xl font-bold">{alerts.unread_count}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Trend Chart + AI Insight */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Trend Chart */}
          <div className="lg:col-span-2 qms-card" data-testid="trend-chart">
            <div className="qms-card-header flex items-center justify-between">
              <span className="flex items-center gap-2"><BarChart3 size={16} /> {t('trendAnalysis.complianceScoreTrend')}</span>
              <span className="badge badge-cyan">{trends?.trend_data?.length || 0} {t('trendAnalysis.months')}</span>
            </div>
            <div className="qms-card-body">
              {trends?.trend_data?.length > 0 ? (
                <>
                  <div className="bar-chart h-48">
                    {trends.trend_data.map((td, i) => (
                      <div
                        key={i}
                        className="bar"
                        style={{ height: `${td.avg_score}%`, backgroundColor: td.avg_score >= 70 ? "#10B981" : td.avg_score >= 50 ? "#C9A84C" : "#EF4444" }}
                        title={`${td.month}: ${td.avg_score}% (${td.total_gaps} gaps)`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-3 text-[#8B95A5] text-xs overflow-x-auto gap-2">
                    {trends.trend_data.map((td, i) => <span key={i}>{td.month}</span>)}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-[#8B95A5]">
                  <BarChart3 size={40} className="mb-3 opacity-30" />
                  <p className="text-sm">{t('trendAnalysis.noAssessmentData')}</p>
                  <p className="text-xs mt-1">{t('trendAnalysis.runGapHint')}</p>
                </div>
              )}
            </div>
          </div>

          {/* AI Insight */}
          <div className="qms-card" data-testid="ai-insight-card">
            <div className="qms-card-header flex items-center gap-2">
              <Zap size={16} className="text-[#C9A84C]" /> {t('trendAnalysis.aiInsight')}
            </div>
            <div className="qms-card-body">
              {trends?.ai_insight ? (
                <p className="text-[#d0d5dd] text-sm leading-relaxed">{trends.ai_insight}</p>
              ) : (
                <div className="text-[#8B95A5] text-sm">
                  <p className="mb-2">{t('trendAnalysis.aiInsightEmpty')}</p>
                  <p className="text-xs opacity-60">{t('trendAnalysis.poweredBy')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Standards Breakdown + Alerts */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Standards Breakdown */}
          <div className="qms-card" data-testid="standards-breakdown">
            <div className="qms-card-header flex items-center gap-2">
              <Shield size={16} /> {t('trendAnalysis.standardsPerformance')}
            </div>
            <div className="qms-card-body">
              {trends?.standards_breakdown?.length > 0 ? (
                <div className="space-y-3">
                  {trends.standards_breakdown.map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-white text-sm truncate max-w-[200px]">{s.standard}</span>
                          <span className="text-xs" style={{ color: s.avg_score >= 70 ? "#10B981" : s.avg_score >= 50 ? "#C9A84C" : "#EF4444" }}>{s.avg_score}%</span>
                        </div>
                        <div className="w-full h-2 bg-[#181B23] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${s.avg_score}%`, backgroundColor: s.avg_score >= 70 ? "#10B981" : s.avg_score >= 50 ? "#C9A84C" : "#EF4444" }} />
                        </div>
                      </div>
                      <span className="text-[#8B95A5] text-xs whitespace-nowrap">{s.count} {t('trendAnalysis.assessments')}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#8B95A5] text-sm text-center py-6">{t('trendAnalysis.noStandardsData')}</p>
              )}
            </div>
          </div>

          {/* Compliance Alerts */}
          <div className="qms-card" data-testid="compliance-alerts">
            <div className="qms-card-header flex items-center justify-between">
              <span className="flex items-center gap-2"><AlertTriangle size={16} /> {t('trendAnalysis.complianceAlerts')}</span>
              <div className="flex items-center gap-2">
                {alerts.unread_count > 0 && (
                  <button onClick={markAllRead} className="text-xs text-[#8B95A5] hover:text-white transition-colors flex items-center gap-1" data-testid="mark-all-read-btn">
                    <BellOff size={12} /> {t('trendAnalysis.markAllRead')}
                  </button>
                )}
                <button onClick={runAlertCheck} disabled={runningCheck} className="text-xs text-[#C9A84C] hover:text-[#dfc06a] transition-colors flex items-center gap-1" data-testid="run-check-btn">
                  <RefreshCw size={12} className={runningCheck ? "animate-spin" : ""} /> {t('trendAnalysis.runCheck')}
                </button>
              </div>
            </div>
            <div className="qms-card-body">
              {alerts.alerts.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {alerts.alerts.map((a, i) => (
                    <div key={i} className={`border-l-2 px-3 py-2 rounded-r ${severityColor(a.severity)} ${a.read ? "opacity-50" : ""}`}>
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-medium">{a.title}</p>
                        <span className="text-xs uppercase tracking-wider">{a.severity}</span>
                      </div>
                      <p className="text-xs mt-1 opacity-80">{a.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-[#8B95A5]">
                  <CheckCircle size={32} className="mb-2 text-emerald-500/40" />
                  <p className="text-sm">{t('trendAnalysis.allClear')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default TrendAnalysisPage;
